import React, { useEffect } from 'react';
import { css, keyframes } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';

type VersionHistoryOnboardingSpotlightProps = {
	children: React.ReactNode;
	contentType: string | null | undefined;
	source: string;
};

// These custom styles are neccessary so the SpotlightPulse doesn't get cut off by the bounds of the menu.
const spotlightStyles = css({
	marginLeft: token('space.025'),
	marginRight: token('space.025'),
});

// Migrated pulse animation styles matching the original SpotlightPulse behavior
const baseShadow = `0 0 0 2px ${token('color.border.discovery')}`;
const easing = 'cubic-bezier(0.55, 0.055, 0.675, 0.19)';

const pulseKeyframes = keyframes({
	'0%, 33%': {
		boxShadow: `${baseShadow}, 0 0 0 ${token('color.border.discovery')}`,
	},
	'66%, 100%': {
		boxShadow: `${baseShadow}, 0 0 0 10px rgba(101, 84, 192, 0.01)`,
	},
});

const pulseAnimationStyles = css({
	animationDuration: '3000ms',
	animationIterationCount: 'infinite',
	animationName: pulseKeyframes,
	animationTimingFunction: easing,
	boxShadow: baseShadow,
	borderRadius: token('radius.small'),
	marginLeft: token('space.025'),
	marginRight: token('space.025'),
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
		transition: 'none',
	},
});

export const VersionHistoryOnboardingSpotlight = ({
	children,
	contentType,
	source,
}: VersionHistoryOnboardingSpotlightProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'versionHistorySpotlightPulse',
				attributes: {
					source,
					objectType: contentType,
				},
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (fg('confluence_frontend_spolitlight_ads_migration')) {
		return (
			<div data-testid="version-history-spotlight-pulse" css={pulseAnimationStyles}>
				{children}
			</div>
		);
	}

	return (
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
		<SpotlightPulse data-testId="version-history-spotlight-pulse" radius={3} css={spotlightStyles}>
			{children}
		</SpotlightPulse>
	);
};
